import React, { useEffect, useState, type CSSProperties, useRef } from 'react'
import { FrameVariant } from './api'
import { MissingImplementationError } from '@packages/common/src/errors/common-errors'
import {
  mediumDeviceWidth,
  smallDeviceWidth,
  widgetBaseSizes,
  widgetOffset,
  widgetShadowSize,
} from './constants'
import { InputAPI } from './Input'
import { createContext, useContextSelector } from 'use-context-selector'
import { useMeasureWidgetDimensions } from './use-measure-widget-dimensions'

/*
  We use it
 */
export const parentWindowWidth: { current: number } = {
  // @ts-ignore
  current: null,
}

type Device = 'default' | 'small' | 'medium'

type State = {
  device: Device
  heightOffset: number
  style: {
    right: CSSProperties['right']
    bottom: CSSProperties['bottom']
    left?: CSSProperties['left']
    top?: CSSProperties['top']
    width: CSSProperties['width']
    height?: CSSProperties['height']
  }
}

type Ctx = {
  device: Device
  isTouched: boolean
  heightOffset: number
}

// @ts-ignore
const offsetContainerCtx = createContext<Ctx>()

export const useOffsetContainerCtx = <Selected,>(
  selector: (s: Ctx) => Selected
) => useContextSelector(offsetContainerCtx, selector)

const calculateState = ({
  variant,
  isTouched,
}: {
  variant: FrameVariant
  isTouched: boolean
}): State => {
  switch (variant) {
    case 'classic': {
      if (parentWindowWidth.current >= mediumDeviceWidth) {
        return {
          device: 'medium',
          heightOffset:
            widgetOffset.mediumDevice.bottom + widgetShadowSize.height,
          style: {
            right: `${widgetOffset.mediumDevice.right}px`,
            bottom: `${widgetOffset.mediumDevice.bottom}px`,
            width: `${widgetBaseSizes.classic.mediumDevice}px`,
          },
        }
      }

      if (parentWindowWidth.current >= smallDeviceWidth) {
        return {
          device: 'small',
          heightOffset:
            widgetOffset.smallDevice.bottom + widgetShadowSize.height,
          style: {
            right: `${widgetOffset.smallDevice.right}px`,
            bottom: `${widgetOffset.smallDevice.bottom}px`,
            width: `${widgetBaseSizes.classic.smallDevice}px`,
          },
        }
      }

      if (isTouched) {
        return {
          device: 'default',
          heightOffset: 0,
          style: {
            width: '100vw',
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
            height: '100%',
          },
        }
      }

      const left = `${widgetOffset.default.left}px`
      const right = `${widgetOffset.default.right}px`
      const width = `calc(${widgetBaseSizes.classic.default} - ${left} - ${right})`

      return {
        device: 'default',
        heightOffset: widgetOffset.default.bottom + widgetShadowSize.height,
        style: {
          left,
          right,
          bottom: `${widgetOffset.default.bottom}px`,
          width,
        },
      }
    }
    case 'avatar': {
      throw new MissingImplementationError()
    }
  }
}

type OffsetContainerProps = {
  onUntouchedClick?: () => void
  children?: React.ReactNode
  variant: FrameVariant
  inputApi: React.RefObject<InputAPI>
  isTouched: boolean
  onHeightChange?: (height: number) => void
}

export const OffsetContainer = ({
  onUntouchedClick,
  children,
  variant,
  inputApi,
  isTouched,
  onHeightChange,
}: OffsetContainerProps) => {
  const [state, setState] = useState(() =>
    calculateState({ variant, isTouched })
  )
  const ref = useRef<HTMLDivElement>(null)

  useMeasureWidgetDimensions(ref, state.heightOffset, onHeightChange)

  useEffect(() => {
    if (isTouched) {
      setState(calculateState({ variant, isTouched }))
    }

    const handleResize = () => {
      setState(calculateState({ variant, isTouched }))
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isTouched])

  return (
    <div
      className="sp-absolute sp-flex sp-flex-grow flex-col"
      style={state.style}
      ref={ref}
      onClick={() => {
        if (!isTouched) {
          inputApi.current?.focus()
          onUntouchedClick?.()
        }
      }}>
      <offsetContainerCtx.Provider
        value={{
          device: state.device,
          isTouched,
          heightOffset: state.heightOffset,
        }}>
        {children}
      </offsetContainerCtx.Provider>
    </div>
  )
}
